// SerialTable.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  message,
  Tag,
  Select,
  DatePicker,
} from 'antd';
import {
  SearchOutlined,
  FileExcelOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { readItems, aggregate } from '@directus/sdk';
import { getDirectusClient } from '../../lib/directus';
import Layouts from '../../components/Layouts';
import SerialDetailModal from './components/SerialDetailModal';
import { useSelector } from 'react-redux';
import { getGrupos } from '../../services/grupos';
import { getProductos } from '../../services/productos';
import { getBodegas } from '../../services/bodegas';
import {
  getSerialesListSimple,
  getSerialesList,
  getSerialesFilteredCount,
  getserialeslistcount,
  createReportSerials,
  estadoSeriales,
} from '../../services/seriales';
import { getOrdenes } from '../../services/ordenes';
import {
  ObtenerUsuariosPorSuperior,
  ObtenerUsuarioByRole,
} from '../../services/usuarios';
import ExportToExcel from './components/ExportToExcel';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { RangePicker } = DatePicker;

const getStatusColor = estado => {
  switch (estado?.toLowerCase()) {
    case 'activo':
      return 'green';
    case 'facturado':
      return 'blue';
    case 'prestado':
      return 'orange';
    case 'anulado':
      return 'red';
    default:
      return 'default';
  }
};

const SerialTable = () => {
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [transferHistoryLoading, setTransferHistoryLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [searchText, setSearchText] = useState('');
  const [serialHistory, setSerialHistory] = useState([]);
  const [serialHistoryLoading, setSerialHistoryLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedGrupo, setSelectedGrupo] = useState([]);
  const [selectedProducto, setSelectedProducto] = useState([]);
  const [selectedOrden, setSelectedOrden] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');
  const [bodegas, setBodegas] = useState([]);
  const [selectedBodega, setSelectedBodega] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [updateDateRange, setUpdateDateRange] = useState(null);

  // Nuevos estados para los filtros de supervisor y vendedor
  const [supervisoresRuta, setSupervisoresRuta] = useState([]);
  const [vendedoresRuta, setVendedoresRuta] = useState([]);
  const [supervisoresTropas, setSupervisoresTropas] = useState([]);
  const [vendedoresTropas, setVendedoresTropas] = useState([]);
  const [selectedSupervisorRuta, setSelectedSupervisorRuta] = useState('');
  const [selectedVendedorRuta, setSelectedVendedorRuta] = useState('');
  const [selectedSupervisorTropas, setSelectedSupervisorTropas] = useState('');
  const [selectedVendedorTropas, setSelectedVendedorTropas] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const userRoleID = useSelector(state => state.userdata.role?.name);
  const userBodega = useSelector(state => state.userdata.bodega?.id);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const navigate = useNavigate();
  const loadGrupos = async () => {
    try {
      const gruposData = await getGrupos();
      setGrupos(gruposData);
    } catch (error) {
      console.error('Error loading grupos:', error);
      message.error('Error al cargar los grupos');
    }
  };

  const loadProductos = async () => {
    try {
      const productosData = await getProductos();
      setProductos(productosData);
    } catch (error) {
      console.error('Error loading productos:', error);
      message.error('Error al cargar los productos');
    }
  };

  const loadOrdenes = async () => {
    try {
      const ordenesData = await getOrdenes(userRoleID, userBodega);
      setOrdenes(ordenesData);
    } catch (error) {
      console.error('Error loading ordenes:', error);
      message.error('Error al cargar las órdenes');
    }
  };

  const loadBodegas = async () => {
    try {
      const bodegasData = await getBodegas();
      setBodegas(bodegasData);
    } catch (error) {
      console.error('Error loading bodegas:', error);
      message.error('Error al cargar las bodegas');
    }
  };

  const loadEstados = async () => {
    try {
      const estadosData = await estadoSeriales();
      setEstados(estadosData);
    } catch (error) {
      console.error('Error loading estados:', error);
      message.error('Error al cargar los estados');
    }
  };

  const loadSupervisoresRuta = async () => {
    try {
      const response = await ObtenerUsuarioByRole('supervisor_ruta');

      if (response && Array.isArray(response)) {
        setSupervisoresRuta(response);
      } else {
        setSupervisoresRuta([]);
      }
    } catch (error) {
      console.error('Error loading supervisores ruta:', error);
      setSupervisoresRuta([]);
    }
  };

  const loadSupervisoresTropas = async () => {
    try {
      const response = await ObtenerUsuarioByRole('supervisor_tropas');

      if (response && Array.isArray(response)) {
        setSupervisoresTropas(response);
      } else {
        setSupervisoresTropas([]);
      }
    } catch (error) {
      setSupervisoresTropas([]);
    }
  };

  const loadVendedoresRuta = async supervisorId => {
    if (!supervisorId) {
      setVendedoresRuta([]);
      return;
    }

    try {
      const response = await ObtenerUsuariosPorSuperior(supervisorId, -1, 1);

      if (response && Array.isArray(response)) {
        const vendedores = response.filter(user => {
          // Verificar diferentes variaciones del nombre del rol
          const roleName = user.role?.name?.toLowerCase();
          const isVendedor = roleName === 'vendedor rutas';

          return isVendedor;
        });

        setVendedoresRuta(vendedores);
      } else {
        setVendedoresRuta([]);
      }
    } catch (error) {
      console.error('Error loading vendedores ruta:', error);
      setVendedoresRuta([]);
    }
  };

  const loadVendedoresTropas = async supervisorId => {
    if (!supervisorId) {
      setVendedoresTropas([]);
      return;
    }

    try {
      const response = await ObtenerUsuariosPorSuperior(supervisorId, -1, 1);

      if (response && Array.isArray(response)) {
        const vendedores = response.filter(user => {
          // Verificar diferentes variaciones del nombre del rol
          const roleName = user.role?.name?.toLowerCase();
          const isTropa = roleName === 'tropas';

          return isTropa;
        });

        setVendedoresTropas(vendedores);
      } else {
        setVendedoresTropas([]);
      }
    } catch (error) {
      console.error('Error loading vendedores tropas:', error);
      setVendedoresTropas([]);
    }
  };

  const fetchTotalCount = async () => {
    try {
      const countResponse = await getserialeslistcount(userRoleID, userBodega);
      const total = parseInt(countResponse[0]?.countDistinct?.id || 0);
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching total count:', error);
      message.error('Error al cargar el conteo total');
    }
  };
  const checkExportAvailability = () => {
    // Verificar los rangos de fecha activos según el estado
    let activeRange = null;

    // Estados que usan fecha de actualización (facturado, prestado, prestado tropas)
    // IDs: 2=facturado, 3=prestado, 9=prestado tropas
    if (
      selectedEstado === 2 ||
      selectedEstado === 3 ||
      selectedEstado === 9
    ) {
      activeRange = updateDateRange;
    } else if (selectedEstado && selectedEstado !== '') {
      activeRange = dateRange;
    } else {
      // Sin estado seleccionado, verificar ambos rangos
      if (dateRange && updateDateRange) {
        // Si hay ambos rangos, verificar que ambos sean válidos
        const createDiff = dateRange[1]
          ? dateRange[1].diff(dateRange[0], 'days')
          : null;
        const updateDiff = updateDateRange[1]
          ? updateDateRange[1].diff(updateDateRange[0], 'days')
          : null;
        setIsExporting(
          createDiff !== null &&
            updateDiff !== null &&
            createDiff <= 31 &&
            updateDiff <= 31
        );
        return;
      } else {
        // Si solo hay uno, usar ese
        activeRange = dateRange || updateDateRange;
      }
    }

    if (activeRange && activeRange[0] && activeRange[1]) {
      const diffInDays = activeRange[1].diff(activeRange[0], 'days');
      setIsExporting(diffInDays <= 31);
    } else {
      setIsExporting(false);
    }
  };
  const fetchSerials = async (
    page = 1,
    pageSize = 25,
    searchValue = '',
    searchType = 'all',
    dateFilterParam = {}
  ) => {
    setLoading(true);
    try {
      if (!searchValue && !searchType) {
        const searchParts = [
          selectedProducto && selectedProducto.length > 0
            ? selectedProducto.join(',')
            : '',
          selectedGrupo && selectedGrupo.length > 0
            ? selectedGrupo.join(',')
            : '',
          selectedOrden && selectedOrden !== '' ? selectedOrden : '',
          selectedEstado && selectedEstado !== '' ? selectedEstado : '',
          selectedBodega && selectedBodega !== '' ? selectedBodega : '',
          selectedSupervisorRuta || '',
          selectedVendedorRuta || '',
          selectedSupervisorTropas || '',
          selectedVendedorTropas || '',
        ];

        const hasActiveFilters = searchParts.some(part => part !== '');

        if (hasActiveFilters) {
          searchType = 'both';
          searchValue = searchParts.join('|');
        }
      }

      // Usar filtros de fecha pasados como parámetro, o usar los actuales si no se pasan
      const dateFilter =
        Object.keys(dateFilterParam).length > 0
          ? dateFilterParam
          : getDateFilter();

      console.log('fetchSerials - final dateFilter being sent to API:', dateFilter);

      const response = await getSerialesListSimple(
        page,
        pageSize,
        searchValue,
        searchType,
        userRoleID,
        userBodega,
        dateFilter
      );

      const countResponse = await getSerialesFilteredCount(
        searchValue,
        searchType,
        userRoleID,
        userBodega,
        dateFilter
      );

      const filtered = parseInt(countResponse[0]?.countDistinct?.id || 0);
      setFilteredCount(filtered);

      setSerials(response);

      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize,
        total: filtered,
      }));
    } catch (error) {
      console.error('Error fetching serials:', error);
      message.error('Error al cargar los seriales');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferHistory = async serialId => {
    setTransferHistoryLoading(true);
    try {
      const directus = await getDirectusClient();

      const response = await directus.request(
        readItems('historial_traslado', {
          fields: [
            'id',
            'estado',
            'date_created',
            'aceptado_fecha',

            'bodega_salida.nombre',
            'bodega_destino.nombre',

            'acaptado_por.first_name',
            'acaptado_por.last_name',
            'acaptado_por.email',
            'anulado_por.first_name',
            'anulado_por.last_name',
            'anulado_por.email',
          ],
          filter: {
            serial: {
              seriales_id: {
                _in: [serialId],
              },
            },
          },
          sort: ['-date_created'],
        })
      );

      setTransferHistory(response);
    } catch (error) {
      console.error('Error fetching transfer history:', error);
      message.error('Error al cargar el historial de traslados');
    } finally {
      setTransferHistoryLoading(false);
    }
  };

  const fetchSerialHistory = async serialId => {
    setSerialHistoryLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems('historial_seriales', {
          fields: [
            'id',
            'factura_n.id',
            'prestamo.id',
            'prestamo.vendedor.first_name',
            'factura_n.vendedor_factura.first_name',
            'factura_n.vendedor_prestamo.first_name',
            'factura_n.vendedor_factura.superior.first_name',
            'factura_n.vendedor_factura.superior.superior.first_name',
            'factura_n.cliente.first_name',
            'factura_n.cliente.last_name',
            'factura_n.user_created.first_name',
            'tipo.tipo',
            'tipo.id',
            'serial',
            'date_created',
            'date_updated',
          ],
          filter: {
            seriales: {
              seriales_id: {
                _in: [serialId],
              },
            },
          },
          sort: ['-date_created'],
        })
      );
      setSerialHistory(response);
    } catch (error) {
      console.error('Error fetching serial history:', error);
      message.error('Error al cargar el historial del serial');
    } finally {
      setSerialHistoryLoading(false);
    }
  };

  const fetchOrderHistory = async serialId => {
    setOrderHistoryLoading(true);
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readItems('ordenes_decompra', {
          fields: [
            'id',
            'estado.nombre',
            'proveedor.nombre',
            'ingresado_por.first_name',
            'user_created.first_name',
            'date_created',
            'date_updated',
            'productos.producto_detalle.nombre',
            'productos.cantidad',
            'productos.valor_sim',
            'productos.precio_compra',
            'productos.precio_uni_final',
            'productos.descuento_comercial',
          ],
          filter: {
            productos: {
              seriales: {
                id: {
                  _eq: serialId,
                },
              },
            },
          },
        })
      );
      setOrderHistory(response);
    } catch (error) {
      console.error('Error fetching order history:', error);
      message.error('Error al cargar el historial de órdenes');
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadGrupos();
    loadProductos();
    loadOrdenes();
    loadBodegas();
    loadEstados();
    loadSupervisoresRuta();
    loadSupervisoresTropas();
    fetchTotalCount();
    fetchSerials(pagination.current, pagination.pageSize);
  }, []);

  useEffect(() => {
    checkExportAvailability();
  }, [dateRange, updateDateRange, selectedEstado]);

  const handleSearch = value => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchSerials(1, pagination.pageSize, value, 'serial');
  };

  const handleTableChange = pagination => {
    setPagination(pagination);

    let searchType = 'all';
    let searchValue = '';

    if (searchText) {
      searchType = 'serial';
      searchValue = searchText;
    } else {
      // Mantener el orden: producto|grupo|orden|estado|bodega
      const searchParts = [
        selectedProducto && selectedProducto.length > 0
          ? selectedProducto.join(',')
          : '',
        selectedGrupo && selectedGrupo.length > 0
          ? selectedGrupo.join(',')
          : '',
        selectedOrden && selectedOrden !== '' ? selectedOrden : '',
        selectedEstado && selectedEstado !== '' ? selectedEstado : '',
        selectedBodega && selectedBodega !== '' ? selectedBodega : '',
        selectedSupervisorRuta || '',
        selectedVendedorRuta || '',
        selectedSupervisorTropas || '',
        selectedVendedorTropas || '',
      ];

      const hasActiveFilters = searchParts.some(part => part !== '');

      if (hasActiveFilters) {
        searchType = 'both';
        searchValue = searchParts.join('|');
      }
    }

    fetchSerials(
      pagination.current,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleGrupoChange = value => {
    setSelectedGrupo(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      value && value.length > 0 ? value.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleProductoChange = value => {
    setSelectedProducto(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      value && value.length > 0 ? value.join(',') : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleOrdenChange = value => {
    setSelectedOrden(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      value && value !== '' ? value : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleEstadoChange = value => {
    setSelectedEstado(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Limpiar filtros de supervisor y vendedor cuando cambie el estado
    // IDs: 2=facturado, 3=prestado, 9=prestado tropas
    if (value !== 3 && value !== 2) {
      setSelectedSupervisorRuta('');
      setSelectedVendedorRuta('');
      setVendedoresRuta([]);
    }

    if (value !== 9) {
      setSelectedSupervisorTropas('');
      setSelectedVendedorTropas('');
      setVendedoresTropas([]);
    }

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      value && value !== '' ? value : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleBodegaChange = value => {
    setSelectedBodega(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      value && value !== '' ? value : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  // Función helper para crear filtros de fecha dinámicos
  const createDateFilter = (dateRange, dateField) => {
    if (!dateRange || !dateRange[0]) {
      return {};
    }

    // Si solo hay una fecha (no es rango), usar esa fecha
    // Si hay dos fechas, usar como rango
    const startDate = dateRange[0];
    const endDate = dateRange[1] || startDate;

    // Crear fechas en formato ISO con zona horaria
    // Para fecha de inicio: 00:00:00 del día
    const startDateTime = startDate.clone().startOf('day').format();
    // Para fecha de fin: 23:59:59 del día
    const endDateTime = endDate.clone().endOf('day').format();


    // Usar _between con fechas ISO
    const filter = {
      [dateField]: {
        _between: [startDateTime, endDateTime]
      }
    };

    console.log('createDateFilter - filter result:', filter);
    return filter;
  };

  // Función helper para obtener filtros de fecha con valores específicos
  const getDateFilterWithDates = (
    creationDateRange,
    updateDateRange,
    estado = selectedEstado
  ) => {
    const dateFilters = [];

 
    const isUpdateDateState =
      estado === 2 ||
      estado === 3 ||
      estado === 9;
    
   

    // Si hay un estado seleccionado, usar solo el campo de fecha apropiado
    if (estado && estado !== '') {
      if (isUpdateDateState) {
        // Para estados facturado/prestado, usar solo date_updated
        if (updateDateRange && updateDateRange[0]) {
          const updateFilter = createDateFilter(
            updateDateRange,
            'date_updated'
          );
          if (Object.keys(updateFilter).length > 0) {
            return updateFilter;
          }
        }
      } else {
        // Para otros estados, usar solo date_created
        if (creationDateRange && creationDateRange[0]) {
          const creationFilter = createDateFilter(
            creationDateRange,
            'date_created'
          );
          if (Object.keys(creationFilter).length > 0) {
            return creationFilter;
          }
        }
      }
    } else {
      // Si no hay estado seleccionado, permitir ambos filtros independientes

      // Filtro de fecha de creación
      if (creationDateRange && creationDateRange[0]) {
        const creationFilter = createDateFilter(
          creationDateRange,
          'date_created'
        );
        if (Object.keys(creationFilter).length > 0) {
          dateFilters.push(creationFilter);
        }
      }

      // Filtro de fecha de actualización
      if (updateDateRange && updateDateRange[0]) {
        const updateFilter = createDateFilter(updateDateRange, 'date_updated');
        if (Object.keys(updateFilter).length > 0) {
          dateFilters.push(updateFilter);
        }
      }

      // Si hay ambos filtros, combinarlos con AND
      if (dateFilters.length === 2) {
        return {
          _and: dateFilters,
        };
      }
      // Si hay solo uno, devolverlo directamente
      else if (dateFilters.length === 1) {
        return dateFilters[0];
      }
    }

    // Si no hay filtros válidos, devolver objeto vacío
    return {};
  };

  // Función helper para obtener filtros de fecha según el estado (usa estados actuales)
  const getDateFilter = () => {
    const filter = getDateFilterWithDates(dateRange, updateDateRange);
    return filter;
  };

  const handleDateRangeChange = dates => {
    setDateRange(dates);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    
    const customDateFilter = getDateFilterWithDates(
      dates,
      updateDateRange,
      selectedEstado
    );
    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      customDateFilter
    );
  };

  const handleUpdateDateRangeChange = dates => {
    setUpdateDateRange(dates);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Mantener el orden: producto|grupo|orden|estado|bodega
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      selectedVendedorRuta || '',
      selectedSupervisorTropas || '',
      selectedVendedorTropas || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    // Crear el filtro de fecha con las nuevas fechas directamente (usar dates como nuevo updateDateRange)
    const customDateFilter = getDateFilterWithDates(
      dateRange,
      dates,
      selectedEstado
    );
    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      customDateFilter
    );
  };

  const handleSupervisorRutaChange = value => {
    setSelectedSupervisorRuta(value);
    setSelectedVendedorRuta(''); // Reset vendedor when supervisor changes
    setPagination(prev => ({ ...prev, current: 1 }));

    if (value) {
      loadVendedoresRuta(value);
    } else {
      setVendedoresRuta([]);
    }

    // Aplicar filtros
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      value || '',
      '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleVendedorRutaChange = value => {
    setSelectedVendedorRuta(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Aplicar filtros
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      selectedSupervisorRuta || '',
      value || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleSupervisorTropasChange = value => {
    setSelectedSupervisorTropas(value);
    setSelectedVendedorTropas(''); // Reset vendedor when supervisor changes
    setPagination(prev => ({ ...prev, current: 1 }));

    if (value) {
      loadVendedoresTropas(value);
    } else {
      setVendedoresTropas([]);
    }

    // Aplicar filtros
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      '',
      '',
      value || '',
      '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleVendedorTropasChange = value => {
    setSelectedVendedorTropas(value);
    setPagination(prev => ({ ...prev, current: 1 }));

    // Aplicar filtros
    const searchParts = [
      selectedProducto && selectedProducto.length > 0
        ? selectedProducto.join(',')
        : '',
      selectedGrupo && selectedGrupo.length > 0 ? selectedGrupo.join(',') : '',
      selectedOrden && selectedOrden !== '' ? selectedOrden : '',
      selectedEstado && selectedEstado !== '' ? selectedEstado : '',
      selectedBodega && selectedBodega !== '' ? selectedBodega : '',
      '',
      '',
      selectedSupervisorTropas || '',
      value || '',
    ];

    const hasActiveFilters = searchParts.some(part => part !== '');
    let searchType = hasActiveFilters ? 'both' : 'all';
    let searchValue = searchParts.join('|');

    fetchSerials(
      1,
      pagination.pageSize,
      searchValue,
      searchType,
      getDateFilter()
    );
  };

  const handleViewSerial = async record => {
    try {
      // Obtener los datos completos del serial para el modal
      const fullSerialData = await getSerialesList(
        1,
        1,
        record.serial,
        'serial',
        userRoleID,
        userBodega,
        {}
      );

      if (fullSerialData && fullSerialData.length > 0) {
        setSelectedSerial(fullSerialData[0]);
      } else {
        setSelectedSerial(record);
      }

      setModalVisible(true);

      // Fetch all histories in parallel
      await Promise.all([
        fetchTransferHistory(record.id),
        fetchSerialHistory(record.id),
        fetchOrderHistory(record.id),
      ]);
    } catch (error) {
      console.error('Error fetching full serial data:', error);
      // Si falla, usar los datos básicos
      setSelectedSerial(record);
      setModalVisible(true);

      await Promise.all([
        fetchTransferHistory(record.id),
        fetchSerialHistory(record.id),
        fetchOrderHistory(record.id),
      ]);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedSerial(null);
    setTransferHistory([]);
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Construir los parámetros para el reporte según los filtros activos
      let params = {
        timezone_offset: new Date().getTimezoneOffset(),
      };

    
      if (
        selectedEstado === 2 ||
        selectedEstado === 3 ||
        selectedEstado === 9
      ) {
        if (updateDateRange && updateDateRange[0] && updateDateRange[1]) {
          params.fecha_facturacion_start =
            updateDateRange[0].format('YYYY-MM-DD');
          params.fecha_facturacion_end =
            updateDateRange[1].format('YYYY-MM-DD');
        }
      } else if (selectedEstado && selectedEstado !== '') {
        if (dateRange && dateRange[0] && dateRange[1]) {
          params.fecha_creacion_start = dateRange[0].format('YYYY-MM-DD');
          params.fecha_creacion_end = dateRange[1].format('YYYY-MM-DD');
        }
      } else {
        // Sin estado seleccionado, usar ambos si existen
        if (dateRange && dateRange[0] && dateRange[1]) {
          params.fecha_creacion_start = dateRange[0].format('YYYY-MM-DD');
          params.fecha_creacion_end = dateRange[1].format('YYYY-MM-DD');
        }
        if (updateDateRange && updateDateRange[0] && updateDateRange[1]) {
          params.fecha_facturacion_start =
            updateDateRange[0].format('YYYY-MM-DD');
          params.fecha_facturacion_end =
            updateDateRange[1].format('YYYY-MM-DD');
        }
      }

      // Filtros adicionales
      if (selectedVendedorRuta) params.vendedor_id = selectedVendedorRuta;
      if (selectedVendedorTropas) params.vendedor_id = selectedVendedorTropas;
      if (selectedBodega) params.bodega_id = selectedBodega;
      if (selectedGrupo && selectedGrupo.length > 0)
        params.grupo_id = selectedGrupo.join(',');
      if (selectedProducto && selectedProducto.length > 0)
        params.producto_id = selectedProducto.join(',');
      if (selectedEstado) params.estado_serial_id = selectedEstado;

      // Llamar al servicio para generar el reporte
      const res = await createReportSerials(params);

      // Si el servicio retorna un 202, mostrar mensaje de éxito
      if (res && res.status === 202) {
        message.success(
          'El reporte está siendo generado. Por favor, revisa la sección de reportes para descargarlo cuando esté listo.'
        );
      } else if (res && res.status && res.status !== 202) {
        message.error(
          'No se pudo iniciar la generación del reporte. Intenta nuevamente.'
        );
      } else {
        // Fallback: si no hay status, asumir éxito como antes
        message.success(
          'El reporte está siendo generado. Por favor, revisa la sección de reportes para descargarlo cuando esté listo.'
        );
      }
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      message.error('Error al generar el archivo Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedGrupo([]);
    setSelectedProducto([]);
    setSelectedOrden('');
    setSelectedEstado('');
    setSelectedBodega('');
    setSelectedSupervisorRuta('');
    setSelectedVendedorRuta('');
    setSelectedSupervisorTropas('');
    setSelectedVendedorTropas('');
    setSearchText('');
    setDateRange(null);
    setUpdateDateRange(null);
    setVendedoresRuta([]);
    setVendedoresTropas([]);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Serial',
      dataIndex: 'serial',
      key: 'serial',
    },
    {
      title: 'Bodega',
      dataIndex: 'bodega',
      key: 'bodega',
      render: bodega => <p>{bodega?.nombre || 'N/A'}</p>,
    },
    {
      title: 'Estado',
      dataIndex: ['estado', 'estado'],
      key: 'estado',
      render: estado => (
        <Tag color={getStatusColor(estado)}>
          {estado?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewSerial(record)}>
          Ver Serial
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      {/* Filtros */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          style={{ width: 200 }}
          placeholder="Seleccionar bodega"
          allowClear
          value={selectedBodega || undefined}
          onChange={handleBodegaChange}
          onClear={() => {
            setSelectedBodega('');
            fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
          }}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {bodegas.map(bodega => (
            <Option key={bodega.id} value={bodega.id}>
              {bodega.nombre}
            </Option>
          ))}
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Seleccionar grupo"
          allowClear
          mode="multiple"
          value={selectedGrupo}
          onChange={handleGrupoChange}
          onClear={() => {
            setSelectedGrupo([]);
            fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
          }}
          showSearch
          filterOption={false}
          onSearch={value => {
            if (value) {
              const filteredGroups = grupos.filter(grup =>
                `${grup.nombre}`.toLowerCase().includes(value.toLowerCase())
              );
              setGrupos(filteredGroups);
            } else {
              getGrupos();
            }
          }}
        >
          {grupos.map(grupo => (
            <Option key={grupo.id} value={grupo.id}>
              {grupo.nombre}
            </Option>
          ))}
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Seleccionar producto"
          allowClear
          mode="multiple"
          value={selectedProducto}
          onChange={handleProductoChange}
          onClear={() => {
            setSelectedProducto([]);
            fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
          }}
          showSearch
          filterOption={false}
          onSearch={value => {
            if (value) {
              const filteredProducts = productos.filter(prod =>
                `${prod.nombre}`.toLowerCase().includes(value.toLowerCase())
              );
              setProductos(filteredProducts);
            } else {
              getProductos();
            }
          }}
        >
          {productos.map(producto => (
            <Option key={producto.id} value={producto.id}>
              {producto.nombre}
            </Option>
          ))}
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Seleccionar orden"
          allowClear
          value={selectedOrden || undefined}
          onChange={handleOrdenChange}
          onClear={() => {
            setSelectedOrden('');
            fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
          }}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {ordenes.map(orden => (
            <Option key={orden.id} value={orden.id}>
              {orden.numero_factura}
            </Option>
          ))}
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Seleccionar estado"
          allowClear
          value={selectedEstado || undefined}
          onChange={handleEstadoChange}
          onClear={() => {
            setSelectedEstado('');
            fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
          }}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {estados.map(estado => (
            <Option key={estado.id} value={estado.id}>
              {estado.estado}
            </Option>
          ))}
        </Select>

        {/* Filtros de supervisor y vendedor para estados prestado y facturado */}
        {/* IDs: 2=facturado, 3=prestado */}
        {(selectedEstado === 3 || selectedEstado === 2) && (
          <>
            <Select
              style={{ width: 200 }}
              placeholder="Supervisor Ruta"
              allowClear
              value={selectedSupervisorRuta || undefined}
              onChange={handleSupervisorRutaChange}
              onClear={() => {
                setSelectedSupervisorRuta('');
                setSelectedVendedorRuta('');
                setVendedoresRuta([]);
                fetchSerials(
                  1,
                  pagination.pageSize,
                  '',
                  'all',
                  getDateFilter()
                );
              }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {supervisoresRuta.map(supervisor => (
                <Option key={supervisor.id} value={supervisor.id}>
                  {`${supervisor.first_name} ${supervisor.last_name}`}
                </Option>
              ))}
            </Select>

            <Select
              style={{ width: 200 }}
              placeholder="Vendedor Rutas"
              allowClear
              value={selectedVendedorRuta || undefined}
              onChange={handleVendedorRutaChange}
              onClear={() => {
                setSelectedVendedorRuta('');
                fetchSerials(
                  1,
                  pagination.pageSize,
                  '',
                  'all',
                  getDateFilter()
                );
              }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={!selectedSupervisorRuta}
            >
              {vendedoresRuta.map(vendedor => (
                <Option key={vendedor.id} value={vendedor.id}>
                  {`${vendedor.first_name} ${vendedor.last_name}`}
                </Option>
              ))}
            </Select>
          </>
        )}

        {/* Filtros de supervisor y vendedor para estado prestado tropas */}
        {/* ID: 9=prestado tropas */}
        {selectedEstado === 9 && (
          <>
            <Select
              style={{ width: 200 }}
              placeholder="Supervisor Tropas"
              allowClear
              value={selectedSupervisorTropas || undefined}
              onChange={handleSupervisorTropasChange}
              onClear={() => {
                setSelectedSupervisorTropas('');
                setSelectedVendedorTropas('');
                setVendedoresTropas([]);
                fetchSerials(
                  1,
                  pagination.pageSize,
                  '',
                  'all',
                  getDateFilter()
                );
              }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {supervisoresTropas.map(supervisor => (
                <Option key={supervisor.id} value={supervisor.id}>
                  {`${supervisor.first_name} ${supervisor.last_name}`}
                </Option>
              ))}
            </Select>

            <Select
              style={{ width: 200 }}
              placeholder="Tropas"
              allowClear
              value={selectedVendedorTropas || undefined}
              onChange={handleVendedorTropasChange}
              onClear={() => {
                setSelectedVendedorTropas('');
                fetchSerials(
                  1,
                  pagination.pageSize,
                  '',
                  'all',
                  getDateFilter()
                );
              }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={!selectedSupervisorTropas}
            >
              {vendedoresTropas.map(vendedor => (
                <Option key={vendedor.id} value={vendedor.id}>
                  {`${vendedor.first_name} ${vendedor.last_name}`}
                </Option>
              ))}
            </Select>
          </>
        )}

        {/* Mostrar filtros de fecha según el estado seleccionado */}
        {/* IDs: 2=facturado, 3=prestado, 9=prestado tropas */}
        {selectedEstado === 2 ||
        selectedEstado === 3 ||
        selectedEstado === 9 ? (
          // Para estados facturado/prestado, solo mostrar filtro de fecha de actualización
          <RangePicker
            style={{ width: 250 }}
            placeholder={[
              'Fecha actualización inicio',
              'Fecha actualización fin',
            ]}
            format="DD/MM/YYYY"
            value={updateDateRange}
            onChange={handleUpdateDateRangeChange}
            allowClear
          />
        ) : selectedEstado && selectedEstado !== '' ? (
          // Para otros estados específicos, solo mostrar filtro de fecha de creación
          <RangePicker
            style={{ width: 250 }}
            placeholder={['Fecha creación inicio', 'Fecha creación fin']}
            format="DD/MM/YYYY"
            value={dateRange}
            onChange={handleDateRangeChange}
              allowClear
            />
          ) : (
            // Sin estado seleccionado, mostrar ambos filtros
            <>
              <RangePicker
                style={{ width: 250 }}
                placeholder={['Fecha creación inicio', 'Fecha creación fin']}
                format="DD/MM/YYYY"
                value={dateRange}
                onChange={handleDateRangeChange}
                allowClear
              />
            </>
          )}
      </Space>

      {/* Total Count Display */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="Buscar por serial"
            value={searchText}
            onChange={e => handleSearch(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            onClear={() => {
              setSearchText('');
              fetchSerials(1, pagination.pageSize, '', 'all', getDateFilter());
            }}
          />

          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={exportToExcel}
            loading={exportLoading}
            disabled={!isExporting || exportLoading}
          >
            {exportLoading ? 'Exportando...' : 'Exportar a Excel'}
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={() => navigate('/reports')}>Ver reportes</Button>
          <Button
            type="primary"
            danger
            icon={<ClearOutlined />}
            onClick={handleClearFilters}
          >
            Limpiar Filtros
          </Button>
          <span>Total de items: {totalCount}</span>
          {filteredCount !== totalCount && (
            <span>Items filtrados: {filteredCount}</span>
          )}
        </Space>
      </div>

      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={serials}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50'],
        }}
        onChange={handleTableChange}
      />

      {/* Modal de Detalles */}
      <SerialDetailModal
        visible={modalVisible}
        onClose={handleCloseModal}
        selectedSerial={selectedSerial}
        transferHistory={transferHistory}
        transferHistoryLoading={transferHistoryLoading}
        serialHistory={serialHistory}
        serialHistoryLoading={serialHistoryLoading}
        orderHistory={orderHistory}
        orderHistoryLoading={orderHistoryLoading}
      />
    </Layouts>
  );
};

export default SerialTable;
